import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  Table,
  Button,
  Input,
  Space,
  message,
  Popconfirm,
  Tag,
  Typography,
  Tooltip,
  Modal,
  Form,
  Select,
  InputNumber,
  Row,
  Col,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  ShoppingOutlined,
  FilterOutlined,
} from '@ant-design/icons';
import { PartTemplate } from '../../types';
import { partTemplateApi } from '../../services/api';

const { Title, Text } = Typography;
const { Search } = Input;
const { TextArea } = Input;

const PartTemplateList: React.FC = () => {
  const [templates, setTemplates] = useState<PartTemplate[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [availabilityFilter, setAvailabilityFilter] = useState<string>('all');
  const [categories, setCategories] = useState<string[]>([]);
  const [suppliers, setSuppliers] = useState<string[]>([]);
  const [brands, setBrands] = useState<string[]>([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<PartTemplate | null>(null);
  const [form] = Form.useForm();
  const navigate = useNavigate();

  useEffect(() => {
    fetchTemplates();
    fetchCategories();
    fetchSuppliers();
    fetchBrands();
  }, [searchTerm, categoryFilter, availabilityFilter]);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const response = await partTemplateApi.getAll({
        search: searchTerm || undefined,
        category: categoryFilter === 'all' ? undefined : categoryFilter,
        availability: availabilityFilter === 'all' ? undefined : availabilityFilter,
      });
      setTemplates(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching part templates:', error);
      message.error('Failed to fetch part templates');
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await partTemplateApi.getCategories();
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchSuppliers = async () => {
    try {
      const response = await partTemplateApi.getSuppliers();
      setSuppliers(response.data);
    } catch (error) {
      console.error('Error fetching suppliers:', error);
    }
  };

  const fetchBrands = async () => {
    try {
      const response = await partTemplateApi.getBrands();
      setBrands(response.data);
    } catch (error) {
      console.error('Error fetching brands:', error);
    }
  };

  const handleEdit = (template: PartTemplate) => {
    setEditingTemplate(template);
    form.setFieldsValue(template);
    setModalVisible(true);
  };

  const handleDelete = async (templateId: string) => {
    try {
      await partTemplateApi.delete(templateId);
      message.success('Part template deleted successfully');
      fetchTemplates();
    } catch (error) {
      console.error('Error deleting part template:', error);
      message.error('Failed to delete part template');
    }
  };

  const handleSaveTemplate = async () => {
    try {
      const values = await form.validateFields();

      if (editingTemplate) {
        await partTemplateApi.update(editingTemplate.id, values);
        message.success('Part template updated successfully');
      } else {
        await partTemplateApi.create({ id: values.id, ...values });
        message.success('Part template created successfully');
      }

      setModalVisible(false);
      setEditingTemplate(null);
      form.resetFields();
      fetchTemplates();
    } catch (error) {
      console.error('Error saving part template:', error);
      message.error('Failed to save part template');
    }
  };

  const handleNewTemplate = () => {
    setEditingTemplate(null);
    form.resetFields();
    setModalVisible(true);
  };

  const getAvailabilityColor = (availability: string) => {
    switch (availability) {
      case 'in_stock': return 'green';
      case 'order_required': return 'orange';
      case 'backordered': return 'red';
      case 'discontinued': return 'default';
      default: return 'default';
    }
  };

  const getAvailabilityText = (availability: string) => {
    switch (availability) {
      case 'in_stock': return 'In Stock';
      case 'order_required': return 'Order Required';
      case 'backordered': return 'Backordered';
      case 'discontinued': return 'Discontinued';
      default: return availability;
    }
  };

  const columns = [
    {
      title: 'Part Number',
      dataIndex: 'part_number',
      key: 'part_number',
      width: 120,
      render: (partNumber: string) => (
        <Text code style={{ fontSize: '12px' }}>{partNumber}</Text>
      ),
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (name: string, record: PartTemplate) => (
        <div>
          <Text strong>{name}</Text>
          <br />
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {record.description}
          </Text>
        </div>
      ),
    },
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
      width: 120,
      render: (category: string) => (
        <Tag color="blue">{category}</Tag>
      ),
    },
    {
      title: 'Price',
      dataIndex: 'unit_price',
      key: 'unit_price',
      width: 100,
      align: 'right' as const,
      render: (price: number) => `$${price.toFixed(2)}`,
    },
    {
      title: 'Availability',
      dataIndex: 'availability',
      key: 'availability',
      width: 120,
      render: (availability: string) => (
        <Tag color={getAvailabilityColor(availability)}>
          {getAvailabilityText(availability)}
        </Tag>
      ),
    },
    {
      title: 'Brand',
      dataIndex: 'brand',
      key: 'brand',
      width: 100,
      render: (brand: string) => brand || '-',
    },
    {
      title: 'Supplier',
      dataIndex: 'supplier',
      key: 'supplier',
      width: 120,
      render: (supplier: string) => supplier || '-',
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      align: 'center' as const,
      render: (_: any, record: PartTemplate) => (
        <Space size="small">
          <Tooltip title="Edit">
            <Button
              type="text"
              size="small"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
          <Popconfirm
            title="Delete Template"
            description="Are you sure you want to delete this part template?"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Tooltip title="Delete">
              <Button
                type="text"
                size="small"
                icon={<DeleteOutlined />}
                danger
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Card>
        <div style={{ marginBottom: '24px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <ShoppingOutlined style={{ fontSize: '24px', color: '#1890ff' }} />
              <Title level={3} style={{ margin: 0 }}>Part Templates</Title>
            </div>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleNewTemplate}
            >
              New Template
            </Button>
          </div>

          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} md={6}>
              <Search
                placeholder="Search parts..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onSearch={fetchTemplates}
                allowClear
              />
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Select
                style={{ width: '100%' }}
                placeholder="Filter by category"
                value={categoryFilter}
                onChange={setCategoryFilter}
                options={[
                  { label: 'All Categories', value: 'all' },
                  ...categories.map(cat => ({ label: cat, value: cat })),
                ]}
              />
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Select
                style={{ width: '100%' }}
                placeholder="Filter by availability"
                value={availabilityFilter}
                onChange={setAvailabilityFilter}
                options={[
                  { label: 'All Availability', value: 'all' },
                  { label: 'In Stock', value: 'in_stock' },
                  { label: 'Order Required', value: 'order_required' },
                  { label: 'Backordered', value: 'backordered' },
                  { label: 'Discontinued', value: 'discontinued' },
                ]}
              />
            </Col>
          </Row>
        </div>

        <Table
          columns={columns}
          dataSource={templates}
          rowKey="id"
          loading={loading}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} parts`,
          }}
          scroll={{ x: 'max-content' }}
        />
      </Card>

      <Modal
        title={editingTemplate ? 'Edit Part Template' : 'New Part Template'}
        open={modalVisible}
        onOk={handleSaveTemplate}
        onCancel={() => {
          setModalVisible(false);
          setEditingTemplate(null);
          form.resetFields();
        }}
        width={700}
        destroyOnHidden
      >
        <Form
          form={form}
          layout="vertical"
          requiredMark={false}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="id"
                label="Template ID"
                rules={[{ required: true, message: 'Please enter template ID' }]}
              >
                <Input
                  placeholder="e.g., oil-filter-001"
                  disabled={!!editingTemplate}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="part_number"
                label="Part Number"
                rules={[{ required: true, message: 'Please enter part number' }]}
              >
                <Input placeholder="e.g., OF-12345" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="name"
            label="Part Name"
            rules={[{ required: true, message: 'Please enter part name' }]}
          >
            <Input placeholder="e.g., Engine Oil Filter" />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="category"
                label="Category"
                rules={[{ required: true, message: 'Please enter category' }]}
              >
                <Input placeholder="e.g., Filters" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="availability"
                label="Availability"
                rules={[{ required: true, message: 'Please select availability' }]}
              >
                <Select
                  placeholder="Select availability"
                  options={[
                    { label: 'In Stock', value: 'in_stock' },
                    { label: 'Order Required', value: 'order_required' },
                    { label: 'Backordered', value: 'backordered' },
                    { label: 'Discontinued', value: 'discontinued' },
                  ]}
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="description"
            label="Description"
            rules={[{ required: true, message: 'Please enter description' }]}
          >
            <TextArea
              rows={3}
              placeholder="Detailed description of the part..."
            />
          </Form.Item>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="unit_price"
                label="Unit Price ($)"
                rules={[{ required: true, message: 'Please enter unit price' }]}
              >
                <InputNumber
                  min={0}
                  step={0.01}
                  placeholder="29.99"
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="brand"
                label="Brand"
              >
                <Input placeholder="e.g., Bosch" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="supplier"
                label="Supplier"
              >
                <Input placeholder="e.g., AutoZone" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="notes"
            label="Notes"
          >
            <TextArea
              rows={2}
              placeholder="Additional notes about this part..."
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default PartTemplateList;